// ============================================================
//  src/pages/AddressPage.jsx
// ============================================================

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";    // ← NEW
import { Card, Button, Toast } from "../components/UI";
import { supabase } from "../utils/supabase";
import config from "../config";

// ── Supabase helpers ──────────────────────────────────────────

async function getSavedAddresses(userId) {
  const { data, error } = await supabase
    .from("addresses")
    .select("id, label, full_name, phone, line1, line2, city, state, pincode, is_default")
    .eq("user_id", userId)
    .order("is_default", { ascending: false });
  if (error) throw new Error(error.message);
  return data;
}

async function saveAddress(userId, address, isFirst = false) {
  const { data, error } = await supabase
    .from("addresses")
    .insert([{ ...address, user_id: userId, is_default: isFirst }])
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data;
}

async function setDefaultAddress(addressId, userId) {
  await supabase.from("addresses").update({ is_default: false }).eq("user_id", userId);
  const { error } = await supabase.from("addresses").update({ is_default: true }).eq("id", addressId);
  if (error) throw new Error(error.message);
}

async function deleteAddress(addressId) {
  const { error } = await supabase.from("addresses").delete().eq("id", addressId);
  if (error) throw new Error(error.message);
}

// ── Validators ────────────────────────────────────────────────

const validators = {
  full_name(v) {
    if (!v.trim())                          return "Full name is required.";
    if (v.trim().length < 2)               return "Name must be at least 2 characters.";
    if (!/^[a-zA-Z\s'-]+$/.test(v.trim())) return "Name can only contain letters, spaces, hyphens or apostrophes.";
    return "";
  },
  phone(v) {
    if (!v.trim())                          return "Phone number is required.";
    const digits = v.replace(/\D/g, "");
    if (digits.length < 10)                return "Phone number must be at least 10 digits.";
    if (digits.length > 15)                return "Phone number must be under 15 digits.";
    return "";
  },
  line1(v) {
    if (!v.trim())                          return "Address Line 1 is required.";
    if (v.trim().length < 5)               return "Please enter a valid address (min 5 characters).";
    return "";
  },
  line2() { return ""; },
  city(v) {
    if (!v.trim())                          return "City is required.";
    if (!/^[a-zA-Z\s]+$/.test(v.trim()))   return "City name can only contain letters.";
    return "";
  },
  state(v) {
    if (!v.trim())                          return "State is required.";
    if (!/^[a-zA-Z\s]+$/.test(v.trim()))   return "State name can only contain letters.";
    return "";
  },
  pincode(v) {
    if (!v.trim())                          return "Pincode is required.";
    if (!/^\d{6}$/.test(v.trim()))          return "Pincode must be exactly 6 digits.";
    return "";
  },
};

function FieldError({ message }) {
  if (!message) return null;
  return (
    <div style={{
      fontSize: 11, color: "#dc2626",
      marginTop: -8, marginBottom: 10,
      paddingLeft: 4, display: "flex", alignItems: "center", gap: 4,
    }}>
      ⚠ {message}
    </div>
  );
}

function ValidatedInput({ label, type = "text", placeholder, value, onChange, onBlur, error, touched, maxLength, disabled }) {
  const borderColor = disabled ? "#e5e7eb" : touched ? (error ? "#dc2626" : "#16a34a") : "#e5e7eb";
  const bgColor     = disabled ? "#f3f4f6" : touched ? (error ? "#fef2f2" : "#f0fdf4") : "#f9fafb";
  return (
    <div style={{ marginBottom: error && touched ? 4 : 14 }}>
      <label style={{
        fontSize: 11, fontWeight: 700, color: "#888",
        letterSpacing: 0.5, textTransform: "uppercase",
        display: "block", marginBottom: 5,
      }}>
        {label}
      </label>
      <input
        type={type} placeholder={placeholder} value={value}
        onChange={onChange} onBlur={onBlur} disabled={disabled} maxLength={maxLength}
        style={{
          width: "100%", padding: "11px 13px", fontSize: 14,
          border: `1.5px solid ${borderColor}`,
          borderRadius: 10, background: bgColor,
          outline: "none", boxSizing: "border-box",
          color: disabled ? "#aaa" : config.colors.text,
          cursor: disabled ? "not-allowed" : "text",
          transition: "border-color 0.2s, background 0.2s",
        }}
      />
    </div>
  );
}

// ── Address Form ──────────────────────────────────────────────

const EMPTY_FORM    = { label: "Home", full_name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "" };
const REQUIRED_KEYS = ["full_name", "phone", "line1", "city", "state", "pincode"];

function AddressForm({ onSave, onCancel, saving, isNewUser }) {
  const [form, setForm]               = useState(EMPTY_FORM);
  const [touched, setTouched]         = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const submitting                    = useRef(false);

  function handleChange(key) {
    return (e) => {
      const value = e.target.value;
      setForm((f) => ({ ...f, [key]: value }));
      if (touched[key]) setFieldErrors((prev) => ({ ...prev, [key]: validators[key]?.(value) || "" }));
    };
  }
  function handleBlur(key) {
    return () => {
      setTouched((prev) => ({ ...prev, [key]: true }));
      setFieldErrors((prev) => ({ ...prev, [key]: validators[key]?.(form[key]) || "" }));
    };
  }
  function validateAll() {
    const errs = {}; const touch = {};
    REQUIRED_KEYS.forEach((k) => { touch[k] = true; errs[k] = validators[k]?.(form[k]) || ""; });
    setFieldErrors(errs); setTouched(touch);
    return Object.values(errs).every((e) => !e);
  }
  function handleSubmit() {
    if (submitting.current || saving) return;
    if (!validateAll()) return;
    submitting.current = true;
    onSave(form);
    setTimeout(() => { submitting.current = false; }, 500);
  }

  return (
    <Card>
      {isNewUser && (
        <div style={{
          background: "#f0fff4", border: `1px solid ${config.colors.primary}44`,
          borderRadius: 12, padding: "14px 16px", marginBottom: 18,
          display: "flex", alignItems: "flex-start", gap: 12,
        }}>
          <span style={{ fontSize: 26 }}>📍</span>
          <div>
            <div style={{ fontWeight: 800, fontSize: 14, color: config.colors.primary, marginBottom: 3 }}>
              Save your address to continue
            </div>
            <div style={{ fontSize: 13, color: "#777", lineHeight: 1.5 }}>
              We need a delivery address before you can proceed to payment.
            </div>
          </div>
        </div>
      )}

      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#888", letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 8 }}>
          Address Type
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {[["Home", "🏠"], ["Work", "🏢"], ["Other", "📌"]].map(([l, icon]) => (
            <button key={l} className="btn-press" onClick={() => setForm((f) => ({ ...f, label: l }))}
              style={{
                background: form.label === l ? config.colors.primary : "#fff",
                color:      form.label === l ? "#fff" : config.colors.primary,
                border:     `2px solid ${form.label === l ? config.colors.primary : config.colors.cardBorder}`,
                borderRadius: 20, padding: "7px 16px",
                fontSize: 12, fontWeight: 700, cursor: "pointer", transition: "all 0.18s",
              }}
            >
              {icon} {l}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <ValidatedInput label="Full Name *" placeholder="Your full name" value={form.full_name} onChange={handleChange("full_name")} onBlur={handleBlur("full_name")} error={fieldErrors.full_name} touched={touched.full_name} />
          <FieldError message={touched.full_name && fieldErrors.full_name} />
        </div>
        <div>
          <ValidatedInput label="Phone Number *" type="tel" placeholder="+91 98765 43210" value={form.phone} onChange={handleChange("phone")} onBlur={handleBlur("phone")} error={fieldErrors.phone} touched={touched.phone} maxLength={15} />
          <FieldError message={touched.phone && fieldErrors.phone} />
        </div>
      </div>

      <ValidatedInput label="Address Line 1 *" placeholder="House / Flat / Building no., Street name" value={form.line1} onChange={handleChange("line1")} onBlur={handleBlur("line1")} error={fieldErrors.line1} touched={touched.line1} />
      <FieldError message={touched.line1 && fieldErrors.line1} />

      <ValidatedInput label="Area / Landmark (Optional)" placeholder="Near Park, Colony name..." value={form.line2} onChange={handleChange("line2")} onBlur={handleBlur("line2")} error={fieldErrors.line2} touched={touched.line2} />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
        <div>
          <ValidatedInput label="City *" placeholder="Chennai" value={form.city} onChange={handleChange("city")} onBlur={handleBlur("city")} error={fieldErrors.city} touched={touched.city} />
          <FieldError message={touched.city && fieldErrors.city} />
        </div>
        <div>
          <ValidatedInput label="State *" placeholder="Tamil Nadu" value={form.state} onChange={handleChange("state")} onBlur={handleBlur("state")} error={fieldErrors.state} touched={touched.state} />
          <FieldError message={touched.state && fieldErrors.state} />
        </div>
        <div>
          <ValidatedInput label="Pincode *" type="tel" placeholder="600001" value={form.pincode} onChange={handleChange("pincode")} onBlur={handleBlur("pincode")} error={fieldErrors.pincode} touched={touched.pincode} maxLength={6} />
          <FieldError message={touched.pincode && fieldErrors.pincode} />
        </div>
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
        {onCancel && <Button variant="outline" fullWidth onClick={onCancel} disabled={saving}>Cancel</Button>}
        <Button fullWidth onClick={handleSubmit} disabled={saving} style={{ opacity: saving ? 0.7 : 1, fontSize: 15, padding: "14px 0" }}>
          {saving ? "⏳ Saving..." : isNewUser ? "Save & Continue to Payment →" : "💾 Save Address"}
        </Button>
      </div>
    </Card>
  );
}

// ── Address Card ──────────────────────────────────────────────

function AddressCard({ address, selected, onSelect, onSetDefault, onDelete }) {
  const labelIcon = address.label === "Home" ? "🏠" : address.label === "Work" ? "🏢" : "📌";
  return (
    <div className="btn-press" onClick={() => onSelect(address)} style={{
      background: selected ? `linear-gradient(135deg, ${config.colors.primary}11, ${config.colors.primaryLight}22)` : "#fff",
      border: `2px solid ${selected ? config.colors.primary : config.colors.cardBorder}`,
      borderRadius: 14, padding: "16px 16px", cursor: "pointer",
      position: "relative", marginBottom: 12,
      boxShadow: selected ? `0 0 0 3px ${config.colors.primary}22` : "0 2px 10px rgba(0,0,0,0.06)",
      transition: "all 0.2s",
    }}>
      {selected && (
        <div style={{
          position: "absolute", top: 14, right: 14, width: 22, height: 22, borderRadius: "50%",
          background: config.colors.primary, display: "flex", alignItems: "center", justifyContent: "center",
          color: "#fff", fontSize: 12, fontWeight: 900,
        }}>✓</div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 16 }}>{labelIcon}</span>
        <span style={{ fontSize: 11, fontWeight: 800, color: config.colors.primary, textTransform: "uppercase", letterSpacing: 0.5 }}>
          {address.label}
        </span>
        {address.is_default && (
          <span style={{ background: "#f0fff4", border: `1px solid ${config.colors.primary}44`, color: config.colors.primary, borderRadius: 20, padding: "2px 10px", fontSize: 10, fontWeight: 800, letterSpacing: 0.3 }}>
            ✅ Default
          </span>
        )}
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, color: config.colors.text, marginBottom: 4 }}>{address.full_name}</div>
      <div style={{ fontSize: 13, color: "#666", lineHeight: 1.6, marginBottom: 6 }}>
        {address.line1}{address.line2 ? `, ${address.line2}` : ""}<br />
        {address.city}, {address.state} – {address.pincode}
      </div>
      <div style={{ fontSize: 12, color: "#999" }}>📞 {address.phone}</div>
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        {!address.is_default && (
          <button className="btn-press" onClick={(e) => { e.stopPropagation(); onSetDefault(address.id); }}
            style={{ background: "#f0f7ee", border: `1px solid ${config.colors.cardBorder}`, borderRadius: 8, padding: "5px 12px", fontSize: 11, fontWeight: 700, color: config.colors.primary, cursor: "pointer" }}>
            Set as Default
          </button>
        )}
        <button className="btn-press" onClick={(e) => { e.stopPropagation(); onDelete(address.id); }}
          style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "5px 12px", fontSize: 11, fontWeight: 700, color: "#dc2626", cursor: "pointer" }}>
          🗑 Remove
        </button>
      </div>
    </div>
  );
}

function EmptyAddresses({ onAdd }) {
  return (
    <div style={{ textAlign: "center", padding: "48px 20px" }}>
      <div style={{ fontSize: 56, marginBottom: 14 }}>🗺️</div>
      <div className="font-display" style={{ fontSize: 18, fontWeight: 800, color: config.colors.primary, marginBottom: 6 }}>No Saved Addresses</div>
      <div style={{ fontSize: 14, color: "#aaa", marginBottom: 22 }}>Add a delivery address to continue.</div>
      <Button onClick={onAdd}>+ Add Address</Button>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────

export default function AddressPage({ auth, onProceed, onBack }) {   // ← removed setPage
  const navigate = useNavigate();                                      // ← NEW
  const { user } = auth;

  const [addresses, setAddresses]     = useState([]);
  const [selected, setSelected]       = useState(null);
  const [showForm, setShowForm]       = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [saving, setSaving]           = useState(false);
  const [toast, setToast]             = useState(null);

  // Redirect unauthenticated users
  useEffect(() => {
    if (!user) navigate("/signin");    // ← was setPage("signin")
  }, [user]); // eslint-disable-line

  if (!user) return null;

  const isNewUser = !pageLoading && addresses.length === 0;

  useEffect(() => {
    let cancelled = false;
    setPageLoading(true);
    getSavedAddresses(user.id)
      .then((list) => {
        if (cancelled) return;
        setAddresses(list);
        const def = list.find((a) => a.is_default) || list[0];
        if (def) setSelected(def);
        if (list.length === 0) setShowForm(true);
      })
      .catch((e) => { if (!cancelled) setToast("⚠️ " + e.message); })
      .finally(() => { if (!cancelled) setPageLoading(false); });
    return () => { cancelled = true; };
  }, [user.id]); // eslint-disable-line

  async function handleSave(form) {
    setSaving(true);
    try {
      const isFirst = addresses.length === 0;
      const saved   = await saveAddress(user.id, form, isFirst);
      setAddresses((prev) => isFirst ? [{ ...saved, is_default: true }] : [...prev, saved]);
      setSelected(saved);
      setShowForm(false);
      setToast("Address saved successfully! 🎉");
    } catch (e) { setToast("⚠️ " + e.message); }
    finally { setSaving(false); }
  }

  async function handleSetDefault(id) {
    setAddresses((prev) => prev.map((a) => ({ ...a, is_default: a.id === id })));
    try {
      await setDefaultAddress(id, user.id);
      setToast("Default address updated!");
    } catch (e) {
      getSavedAddresses(user.id).then(setAddresses).catch(() => {});
      setToast("⚠️ " + e.message);
    }
  }

  async function handleDelete(id) {
    const prev    = addresses;
    const updated = addresses.filter((a) => a.id !== id);
    setAddresses(updated);
    if (selected?.id === id) {
      setSelected(updated.find((a) => a.is_default) || updated[0] || null);
    }
    try {
      await deleteAddress(id);
      setToast("Address removed.");
    } catch (e) { setAddresses(prev); setToast("⚠️ " + e.message); }
  }

  function handleProceed() {
    if (!selected) return;
    if (onProceed) onProceed(selected);
    else navigate("/cart");            // ← was setPage("cart")
  }

  return (
    <div className="cart-page-container">
      <div className="address-header" style={{
        background: `linear-gradient(135deg, ${config.colors.primary}, ${config.colors.primaryLight})`,
        borderRadius: 20, padding: "22px 20px", color: "#fff", marginBottom: 18,
      }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>📦</div>
        <div className="font-display" style={{ fontSize: 20, fontWeight: 800 }}>Delivery Address</div>
        <div style={{ opacity: 0.85, fontSize: 13, marginTop: 4 }}>
          {isNewUser ? "Add an address to get started" : pageLoading ? "\u00a0" : `${addresses.length} saved address${addresses.length !== 1 ? "es" : ""}`}
        </div>
      </div>

      {pageLoading && (
        <>
          <style>{`@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }`}</style>
          {[1, 2].map((i) => (
            <div key={i} style={{ background: "#fff", borderRadius: 14, padding: "16px 16px", marginBottom: 12, border: `1px solid ${config.colors.cardBorder}`, boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}>
              {[80, "60%", "80%", 60].map((w, j) => (
                <div key={j} style={{ background: "linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite", borderRadius: 6, height: 13, width: w, marginBottom: 10 }} />
              ))}
            </div>
          ))}
        </>
      )}

      {!pageLoading && (
        <>
          {addresses.length > 0 && !showForm && (
            <button className="btn-press" onClick={() => setShowForm(true)} style={{
              width: "100%", background: "#fff", border: `2px dashed ${config.colors.primary}`,
              borderRadius: 14, padding: "13px 0", fontWeight: 800, fontSize: 14,
              color: config.colors.primary, cursor: "pointer", marginBottom: 16,
            }}>
              + Add New Address
            </button>
          )}

          {showForm && (
            <AddressForm
              onSave={handleSave}
              onCancel={addresses.length > 0 ? () => setShowForm(false) : null}
              saving={saving}
              isNewUser={isNewUser}
            />
          )}

          {!showForm && addresses.length === 0 && <EmptyAddresses onAdd={() => setShowForm(true)} />}

          {!showForm && addresses.length > 0 && (
            <>
              {addresses.map((a) => (
                <AddressCard key={a.id} address={a} selected={selected?.id === a.id} onSelect={setSelected} onSetDefault={handleSetDefault} onDelete={handleDelete} />
              ))}
              <div className="address-proceed-bar">
                {selected && (
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 10, fontWeight: 800, color: "#bbb", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 2 }}>📦 Delivering to</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: config.colors.text }}>{selected.full_name} · {selected.city}, {selected.pincode}</div>
                  </div>
                )}
                {onBack && <Button variant="outline" fullWidth onClick={onBack} style={{ marginBottom: 10 }}>← Back to Cart</Button>}
                <Button className="proceed-btn" fullWidth onClick={handleProceed} disabled={!selected}>
                  {selected ? "Continue to Payment →" : "Select an address above"}
                </Button>
              </div>
            </>
          )}
        </>
      )}

      {toast && <Toast message={toast} onDone={() => setToast(null)} />}
    </div>
  );
}
